import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Plus, Eye, Edit2, MoreVertical, Send, CheckCircle,
    XCircle, Ban, FileText, Clock
} from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, Table, Pagination, Button, Badge,
    SearchInput, Select, Dropdown, DropdownItem, DropdownDivider,
    ConfirmDialog, toast, Tabs
} from '../../components/common';
import { purchaseRequestApi } from '../../services/api';
import useAuthStore from '../../stores/authStore';
import './PurchaseRequests.css';

export default function PurchaseRequestList() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { user } = useAuthStore();
    const [activeTab, setActiveTab] = useState('all');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [actionPR, setActionPR] = useState(null);
    const [actionType, setActionType] = useState('');

    // Determine which API to call based on tab
    const getQueryFn = () => {
        switch (activeTab) {
            case 'my':
                return () => purchaseRequestApi.getMyRequests({ page, limit: pageSize, search, status: statusFilter });
            case 'pending':
                return () => purchaseRequestApi.getPendingApprovals();
            default:
                return () => purchaseRequestApi.list({ page, limit: pageSize, search, status: statusFilter });
        }
    };

    const { data, isLoading } = useQuery({
        queryKey: ['purchaseRequests', activeTab, page, pageSize, search, statusFilter],
        queryFn: getQueryFn(),
        select: (res) => res.data
    });

    // Status mutations
    const actionMutation = useMutation({
        mutationFn: ({ id, action, data }) => {
            switch (action) {
                case 'submit': return purchaseRequestApi.submit(id);
                case 'approve': return purchaseRequestApi.approve(id, data);
                case 'reject': return purchaseRequestApi.reject(id, data);
                case 'cancel': return purchaseRequestApi.cancel(id, data);
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            const messages = {
                submit: 'submitted',
                approve: 'approved',
                reject: 'rejected',
                cancel: 'cancelled'
            };
            toast.success(`Purchase Request ${messages[action]} successfully`);
            queryClient.invalidateQueries(['purchaseRequests']);
            setActionPR(null);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const getStatusBadge = (status) => {
        const config = {
            'draft': { variant: 'default', icon: FileText },
            'pending': { variant: 'warning', icon: Clock },
            'approved': { variant: 'success', icon: CheckCircle },
            'rejected': { variant: 'danger', icon: XCircle },
            'cancelled': { variant: 'default', icon: Ban }
        };
        const { variant } = config[status] || { variant: 'default' };
        return <Badge variant={variant}>{status}</Badge>;
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(value || 0);
    };

    const columns = [
        {
            key: 'prNumber',
            title: 'PR Number',
            dataIndex: 'prNumber',
            width: 150,
            render: (num) => <span className="pr-number">{num}</span>
        },
        {
            key: 'title',
            title: 'Title',
            dataIndex: 'title',
            render: (title, row) => (
                <div className="pr-title-cell">
                    <span className="pr-title">{title}</span>
                    <span className="pr-requester">by {row.requester?.fullName || 'Unknown'}</span>
                </div>
            )
        },
        {
            key: 'department',
            title: 'Department',
            dataIndex: 'department',
            render: (dept) => dept?.name || '-'
        },
        {
            key: 'totalAmount',
            title: 'Amount',
            dataIndex: 'totalAmount',
            align: 'right',
            render: (amount) => <span className="pr-amount">{formatCurrency(amount)}</span>
        },
        {
            key: 'priority',
            title: 'Priority',
            dataIndex: 'priority',
            width: 100,
            render: (priority) => (
                <Badge variant={priority === 'high' ? 'danger' : priority === 'medium' ? 'warning' : 'default'}>
                    {priority}
                </Badge>
            )
        },
        {
            key: 'status',
            title: 'Status',
            dataIndex: 'status',
            width: 120,
            render: (status) => getStatusBadge(status)
        },
        {
            key: 'createdAt',
            title: 'Created',
            dataIndex: 'createdAt',
            width: 100,
            render: (date) => new Date(date).toLocaleDateString()
        },
        {
            key: 'actions',
            title: '',
            width: 50,
            align: 'center',
            render: (_, row) => (
                <Dropdown
                    trigger={<button className="action-btn"><MoreVertical size={16} /></button>}
                    align="right"
                >
                    {({ close }) => (
                        <>
                            <DropdownItem icon={Eye} onClick={() => { close(); navigate(`/purchase-requests/${row.id}`); }}>
                                View Details
                            </DropdownItem>
                            {row.status === 'draft' && (
                                <>
                                    <DropdownItem icon={Edit2} onClick={() => { close(); navigate(`/purchase-requests/${row.id}/edit`); }}>
                                        Edit
                                    </DropdownItem>
                                    <DropdownItem icon={Send} onClick={() => { close(); setActionPR(row); setActionType('submit'); }}>
                                        Submit
                                    </DropdownItem>
                                </>
                            )}
                            {row.status === 'pending' && (
                                <>
                                    <DropdownDivider />
                                    <DropdownItem icon={CheckCircle} onClick={() => { close(); setActionPR(row); setActionType('approve'); }}>
                                        Approve
                                    </DropdownItem>
                                    <DropdownItem icon={XCircle} danger onClick={() => { close(); setActionPR(row); setActionType('reject'); }}>
                                        Reject
                                    </DropdownItem>
                                </>
                            )}
                            {['draft', 'pending'].includes(row.status) && (
                                <>
                                    <DropdownDivider />
                                    <DropdownItem icon={Ban} danger onClick={() => { close(); setActionPR(row); setActionType('cancel'); }}>
                                        Cancel
                                    </DropdownItem>
                                </>
                            )}
                        </>
                    )}
                </Dropdown>
            )
        }
    ];

    const statusOptions = [
        { value: '', label: 'All Status' },
        { value: 'draft', label: 'Draft' },
        { value: 'pending', label: 'Pending' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'cancelled', label: 'Cancelled' }
    ];

    const tabs = [
        { key: 'all', label: 'All Requests' },
        { key: 'my', label: 'My Requests' },
        { key: 'pending', label: 'Pending Approval' }
    ];

    return (
        <div className="pr-page">
            <PageHeader
                title="Purchase Requests"
                subtitle="Manage purchase requests"
                breadcrumbs={[{ label: 'Purchase Requests', path: '/purchase-requests' }]}
                actions={
                    <Button icon={Plus} onClick={() => navigate('/purchase-requests/create')}>
                        New Request
                    </Button>
                }
            />

            <Card>
                <div className="pr-tabs">
                    {tabs.map(tab => (
                        <button
                            key={tab.key}
                            className={`pr-tab ${activeTab === tab.key ? 'active' : ''}`}
                            onClick={() => { setActiveTab(tab.key); setPage(1); }}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="pr-toolbar">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        onSearch={setSearch}
                        placeholder="Search by PR number or title..."
                        className="pr-search"
                    />
                    <Select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        options={statusOptions}
                        className="pr-filter"
                    />
                </div>

                <Table
                    columns={columns}
                    data={Array.isArray(data) ? data : (data?.data || [])}
                    loading={isLoading}
                    emptyText="No purchase requests found"
                    rowKey="id"
                />

                {data?.meta && (
                    <div className="pr-pagination">
                        <Pagination
                            page={page}
                            totalPages={data.meta.totalPages}
                            pageSize={pageSize}
                            total={data.meta.total}
                            onPageChange={setPage}
                            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
                        />
                    </div>
                )}
            </Card>

            <ConfirmDialog
                isOpen={!!actionPR}
                onClose={() => { setActionPR(null); setActionType(''); }}
                onConfirm={() => actionMutation.mutate({ id: actionPR?.id, action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} Purchase Request`}
                message={`Are you sure you want to ${actionType} "${actionPR?.prNumber}"?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                variant={['reject', 'cancel'].includes(actionType) ? 'danger' : 'primary'}
                loading={actionMutation.isPending}
            />
        </div>
    );
}
